import React, { useEffect, useRef, useState } from 'react';
import { usePractice } from '../contexts/PracticeContext';
import confetti from 'canvas-confetti';
import { shareSpellingList } from '../services/supabase';
import { trackEvent } from '../utils/analytics';

interface PracticeFormProps {
  onCancel?: () => void;
}

const PracticeForm: React.FC<PracticeFormProps> = ({ onCancel }) => {
  const { 
    editingPractice, 
    newPracticeName, 
    setNewPracticeName, 
    newPracticeWords, 
    setNewPracticeWords,
    addPractice,
    updatePractice,
    setShowForm,
    setEditingPractice
  } = usePractice();
  
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  
  const isEditing = !!editingPractice;
  
  const nameInputRef = useRef<HTMLInputElement>(null);
  const wordsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const shareLinkInputRef = useRef<HTMLInputElement>(null);

  // Effect to initialize form values when editing
  useEffect(() => {
    if (editingPractice && !newPracticeName && !newPracticeWords) {
      setNewPracticeName(editingPractice.name);
      setNewPracticeWords(editingPractice.words.join('\n'));
    }
  }, [editingPractice, newPracticeName, newPracticeWords, setNewPracticeName, setNewPracticeWords]);

  // Effect to handle input focus
  useEffect(() => {
    const focusNameInput = () => {
      requestAnimationFrame(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      });
    };
    
    focusNameInput();
    
    const timeouts = [
      setTimeout(focusNameInput, 50),
      setTimeout(focusNameInput, 150),
      setTimeout(focusNameInput, 300)
    ];
    
    return () => timeouts.forEach(clearTimeout);
  }, []);
  
  const handleSave = () => {
    if (!newPracticeName.trim() || !newPracticeWords.trim()) return;
    
    const wordList = newPracticeWords
      .split('\n')
      .map(word => word.trim())
      .filter(word => word.length > 0);
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    
    if (isEditing && editingPractice) {
      updatePractice(Number(editingPractice.id), newPracticeName, wordList);
      
      // Track list update event
      trackEvent('spelling_list_updated', {
        list_name: newPracticeName,
        word_count: wordList.length
      });
    } else {
      addPractice(newPracticeName, wordList);
      
      // Track list creation event
      trackEvent('spelling_list_created', {
        list_name: newPracticeName,
        word_count: wordList.length
      });
      
      // Only clear form state when creating a new practice
      setNewPracticeName('');
      setNewPracticeWords('');
    }
    
    setEditingPractice(null);
    setShowForm(false);
  };
  
  const handleCancel = () => {
    setNewPracticeName('');
    setNewPracticeWords('');
    setEditingPractice(null);
    setShowForm(false);
    if (onCancel) onCancel();
  };
  
  const handleShare = async () => {
    if (!editingPractice) {
      alert('Please save the practice list before sharing.');
      return;
    }
    
    try {
      setIsSharing(true);
      
      const shareId = await shareSpellingList(String(editingPractice.id));
      
      const shareUrl = `${window.location.origin}/shared/${shareId}`;
      
      setShareLink(shareUrl);
      setShowDialog(true);
      
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      setIsSharing(false);
    } catch (error) {
      console.error('Error sharing list:', error);
      setIsSharing(false);
      alert('Error sharing list. Please try again.');
    }
  };
  
  const handleCopyLink = () => {
    if (shareLinkInputRef.current) {
      shareLinkInputRef.current.select();
      document.execCommand('copy');
      
      setCopySuccess(true);
      
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    }
  };
  
  return (
    <>
      <div className="bg-gradient-to-r from-indigo-100 to-purple-100 p-4 sm:p-6 rounded-xl shadow-lg mb-6 sm:mb-8 border-2 border-indigo-200">
        <div className="mb-4 sm:mb-6 text-center">
          <div className="inline-block bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-md">
            <h2 className="text-lg sm:text-xl font-bold flex items-center">
              <span className="text-xl sm:text-2xl mr-2">{isEditing ? '✏️' : '✨'}</span>
              {isEditing ? 'Edit Your Spelling List' : 'Create a Spelling List'}
            </h2>
          </div>
        </div>
        
        <div className="mb-4 sm:mb-6">
          <label htmlFor="practice-name" className="block text-base sm:text-lg font-bold text-indigo-800 mb-2 flex items-center">
            <span className="text-xl sm:text-2xl mr-2">📝</span>
            What's your list called?
          </label>
          <input
            id="practice-name"
            type="text"
            ref={nameInputRef}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-indigo-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-lg shadow-sm"
            value={newPracticeName}
            onChange={(e) => setNewPracticeName(e.target.value)}
            placeholder=""
            autoComplete="off"
            autoFocus={true}
            tabIndex={1}
          />
        </div>
        
        <div className="mb-6 sm:mb-8">
          <label htmlFor="practice-words" className="block text-base sm:text-lg font-bold text-indigo-800 mb-2 flex flex-wrap items-center">
            <span className="text-xl sm:text-2xl mr-2">🔤</span>
            Add your spelling words here!
            <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 w-full sm:w-auto text-xs sm:text-sm font-normal text-indigo-600">(one word on each line)</span>
          </label>
          <div className="relative">
            <textarea
              id="practice-words"
              ref={wordsTextareaRef}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-indigo-300 rounded-lg sm:rounded-xl h-36 sm:h-48 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-lg shadow-sm"
              value={newPracticeWords}
              onChange={(e) => setNewPracticeWords(e.target.value)}
              placeholder=""
              spellCheck="false"
              autoComplete="off"
              tabIndex={2}
            ></textarea>
            <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 text-xs sm:text-sm text-indigo-400">
              {newPracticeWords.split('\n').filter(word => word.trim().length > 0).length} words
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center sm:space-x-6 space-y-4 sm:space-y-0">
          <button
            onClick={handleCancel}
            className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 bg-white text-indigo-700 rounded-full hover:bg-gray-50 transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-base sm:text-lg flex items-center justify-center"
          >
            <span className="text-lg sm:text-xl mr-2">❌</span>
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`px-4 sm:px-6 py-2 sm:py-3 ${isEditing ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600' : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'} text-white rounded-full transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-base sm:text-lg flex items-center justify-center`}
            disabled={!newPracticeName.trim() || !newPracticeWords.trim()}
          >
            <span className="text-lg sm:text-xl mr-2">{isEditing ? '💾' : '🚀'}</span>
            {isEditing ? 'Save Changes' : 'Create My List!'}
          </button>
        </div>
        
        {isEditing && (
          <div className="mt-6 text-center">
            <button
              onClick={handleShare}
              disabled={isSharing}
              className={`inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-full transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-lg`}
            >
              <span className="text-xl mr-2">🔗</span>
              {isSharing ? 'Sharing...' : 'Share'}
            </button>
          </div>
        )}
      </div>
      
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
            <h3 className="text-xl font-bold mb-4 text-indigo-800">Share Your Spelling List</h3>
            <p className="mb-4 text-gray-600">Copy this link to share your spelling list with others:</p>
            
            <div className="flex mb-6">
              <input
                ref={shareLinkInputRef}
                type="text"
                value={shareLink}
                readOnly
                className="w-full px-3 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold ml-2"
              >
                <span className="text-lg mr-1">📋</span>
                Copy
              </button>
            </div>
            
            {copySuccess && (
              <div className="mb-6 p-2 bg-green-100 border border-green-300 text-green-700 rounded-lg text-center">
                <span className="flex items-center justify-center">
                  <span className="text-lg mr-2">✅</span>
                  Link copied to clipboard!
                </span>
              </div>
            )}
            
            <div className="flex justify-center">
              <button
                onClick={() => setShowDialog(false)}
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-full transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-bold text-lg"
              >
                <span className="text-lg mr-2">❌</span>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-yellow-100 border-2 border-yellow-300 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-md mb-4 sm:mb-6">
        <h3 className="font-bold text-yellow-800 flex items-center mb-1 sm:mb-2 text-sm sm:text-base">
          <span className="text-lg sm:text-xl mr-1 sm:mr-2">💡</span>
          Helpful Tips
        </h3>
        <ul className="text-yellow-800 space-y-1 sm:space-y-2 text-xs sm:text-sm">
          <li className="flex items-start">
            <span className="text-base sm:text-lg mr-1 sm:mr-2">•</span>
            Type each word on a new line
          </li>
          <li className="flex items-start">
            <span className="text-base sm:text-lg mr-1 sm:mr-2">•</span>
            You can add as many words as you want
          </li>
        </ul>
      </div>
    </>
  );
};

export default PracticeForm;
